import {getISOTimeString, getISOTimeStringForReview} from '@/utils/commons';
import {getValidMediaData} from '@/pages/SalesTargetUtils';
import {TARGET_TYPES} from '@/utils/constants/saleTargetConstants';
import {isCdaSuperTargetType} from '@/utils/salesTargetStepConfig';
import {
  isProductGroupQuantityType,
  normalizeProductGtins
} from '@/utils/productGroupQuantity';
import {Constants} from '@/utils/constants/commonConstants';

// Helper function to filter display content
export const filterDisplayContent = (displayContent, excludeChild = false) => {
  return Object.keys(displayContent || {})
    .filter((langKey) => !excludeChild || langKey !== 'child')
    .reduce((acc, langKey) => {
      acc[langKey] = displayContent?.[langKey];
      return acc;
    }, {});
};

// Helper function to format all date fields
export const formatDateFields = (salesTargetData) => ({
  activeStartDate: getISOTimeStringForReview(
    salesTargetData?.activeStartDate,
    false,
    true
  ),
  activeEndDate: getISOTimeStringForReview(
    salesTargetData?.activeEndDate,
    false,
    true
  ),
  targetDeliveryDate: getISOTimeStringForReview(
    salesTargetData?.targetDeliveryDate,
    false,
    true
  ),
  displayStartDate: getISOTimeStringForReview(
    salesTargetData?.displayStartDate,
    false,
    true
  ),
  displayEndDate: getISOTimeStringForReview(
    salesTargetData?.displayEndDate,
    false,
    true
  ),
  pointsDeliveryDate: getISOTimeStringForReview(
    salesTargetData?.pointsDeliveryDate,
    false,
    true
  )
});

// Helper function to create metadata object
export const createMetadata = (salesTargetData, tabType) => {
  const mediaFile = salesTargetData?.tabWiseData?.[tabType]?.mediaFile;
  const hasOneFile = mediaFile?.length === 1;
  return {
    productFileName: hasOneFile ? mediaFile[0] : '',
    isProductIdsUploaded: hasOneFile
  };
};

// Helper function to create application object
export const createApplicationObject = () => ({
  createdAt: '',
  updatedAt: '',
  createdBy: null,
  updatedBy: null,
  id: '',
  country: '',
  siteId: '',
  timezone: '',
  pointsMultiplierRuleSet: {
    rules: [
      {
        name: 'Order Channel',
        matchWith: ['online'],
        multiplier: 2.0
      },
      {
        name: 'Order Channel',
        matchWith: ['offline'],
        multiplier: 1.5
      }
    ]
  },
  config: null
});

// Helper function to transform categories to labels
export const transformCategoriesToLabels = async (ruleSet, categoryList) => {
  if (!ruleSet?.rules?.[0]?.includedCategories) {
    return ruleSet;
  }
  const includedCategoryIds = ruleSet.rules[0].includedCategories;
  const excludedCategoryIds = ruleSet.rules[0].excludedCategories || [];
  const includedCategoriesLabels = categoryList
    .filter(
      (cat) =>
        includedCategoryIds.includes(cat.categoryId) ||
        includedCategoryIds.includes(cat.category)
    )
    .map((cat) => cat.category);
  const excludedCategoriesLabels = categoryList
    .filter(
      (cat) =>
        excludedCategoryIds.includes(cat.categoryId) ||
        excludedCategoryIds.includes(cat.category)
    )
    .map((cat) => cat.category);
  return {
    ...ruleSet,
    rules: [
      {
        ...ruleSet.rules[0],
        includedCategories: includedCategoriesLabels,
        excludedCategories: excludedCategoriesLabels
      }
    ]
  };
};

// Helper function to create child object
export const createChildObject = async (
  salesTargetData,
  dateFields,
  categoryList
) => {
  const childRuleSet = await transformCategoriesToLabels(
    salesTargetData?.tabWiseData?.child?.ruleSet,
    categoryList
  );
  return {
    id: '',
    storeList: '',
    targetName: salesTargetData?.targetName || '',
    ruleSet: childRuleSet,
    displayContent: filterDisplayContent(
      salesTargetData?.child?.displayContent
    ),
    targetType: salesTargetData?.targetTypeValue || '',
    awardPointsImmedFlag: false,
    joinCampaignFlag: true,
    VATIncluded: !salesTargetData?.excludeTaxFromGoal,
    cepRefId: salesTargetData?.cepRefId ?? null,
    childRuleSet: null,
    roundPointsValue: salesTargetData?.roundPointsValue || '',
    roundUpPointsFlag: salesTargetData?.roundUpPointsFlag ?? false,
    ...dateFields,
    media: getValidMediaData(salesTargetData),
    status: salesTargetData?.status,
    ignoreSkuList: salesTargetData?.tabWiseData?.child?.ignoreSkuList ?? false,
    offlineOrdersIncluded: salesTargetData?.offlineOrdersIncluded ?? false,
    pointDisplay: null,
    children: [],
    application: createApplicationObject(),
    parent: null,
    totalTasks: null,
    metadata: createMetadata(salesTargetData, 'child')
  };
};

export const formatDataForStep = (formData, step) => {
  switch (step) {
    case 'setup':
      return {
        ...formData,
        displayStartDate: getISOTimeString(
          formData?.activeDisplay
            ? formData?.startDate
            : formData?.displayStartDate,
          false,
          true
        ),
        displayEndDate: getISOTimeString(
          formData?.activeDisplay
            ? formData?.endDate
            : formData?.displayEndDate,
          false,
          true
        ),
        pointsDeliveryDate: getISOTimeString(
          formData?.pointsDeliveryDate,
          false,
          true
        ),
        startDate: getISOTimeString(formData?.startDate, false, true),
        endDate: getISOTimeString(formData?.endDate, false, true),
        activeStartDate: getISOTimeString(
          formData?.activeStartDate,
          false,
          true
        ),
        activeEndDate: getISOTimeString(formData?.activeEndDate, false, true),
        targetDeliveryDate: getISOTimeString(
          formData?.targetDeliveryDate,
          false,
          true
        ),
        targetType: formData?.targetTypeValue || ''
      };
    case 'displayContent':
    case 'products':
      return {
        ...formData,
        displayStartDate: getISOTimeString(
          formData?.activeDisplay
            ? formData?.startDate
            : formData?.displayStartDate,
          false,
          true
        ),
        displayEndDate: getISOTimeString(
          formData?.activeDisplay
            ? formData?.endDate
            : formData?.displayEndDate,
          false,
          true
        ),
        pointsDeliveryDate: getISOTimeString(
          formData?.pointsDeliveryDate,
          false,
          true
        ),
        startDate: getISOTimeString(formData?.startDate, false, true),
        endDate: getISOTimeString(formData?.endDate, false, true),
        targetDeliveryDate: getISOTimeString(
          formData?.targetDeliveryDate,
          false,
          true
        ),
        activeStartDate: getISOTimeString(
          formData?.activeStartDate,
          false,
          true
        ),
        activeEndDate: getISOTimeString(formData?.activeEndDate, false, true)
      };
    default:
      return formData;
  }
};

export const shouldTransformRuleSetOfSalesTarget = (step) => {
  return step === 'products' || step === 'review';
};

export const getDisplayContent = (isPrerequisiteTarget, additionalData) => {
  return isPrerequisiteTarget
    ? additionalData.displayPreReqContentFormData
    : additionalData.displayContentFormData;
};

export const formatDraftNotificationMessage = (hasExistingUuid) => {
  return hasExistingUuid ? 'Updated Sales Target' : 'New Sales Target Drafted';
};

// Helper function to create request object for regular targets
export const createReqObj = async (salesTargetData, categoryList) => {
  const isLinkedTarget =
    salesTargetData?.targetType === TARGET_TYPES.EarlyPaymentTarget ||
    salesTargetData?.targetType === TARGET_TYPES.DropSizeTarget;

  const ruleSet = isLinkedTarget
    ? null
    : await transformCategoriesToLabels(
        salesTargetData?.tabWiseData?.parent?.ruleSet,
        categoryList
      );
  const childRuleSet = isLinkedTarget
    ? null
    : await transformCategoriesToLabels(
        salesTargetData?.childRuleSet,
        categoryList
      );
  const isLayeredTarget =
    salesTargetData?.targetType === TARGET_TYPES.ProductTargetLayered ||
    salesTargetData?.targetType === TARGET_TYPES.MonthlyTargetLayered;
  const isMainTargetOrProdTarget =
    salesTargetData?.targetType === TARGET_TYPES.MainTarget ||
    salesTargetData?.targetType === TARGET_TYPES.ProductTarget ||
    salesTargetData?.targetType === TARGET_TYPES.RackChallenge;

  const result = {
    targetName: salesTargetData?.targetName,
    challengeTransactionId: salesTargetData?.challengeTransactionId || '',
    displayContent: Object.keys(salesTargetData?.displayContent || {})
      .filter((langKey) => langKey !== 'child')
      .reduce((acc, langKey) => {
        acc[langKey] = salesTargetData?.displayContent?.[langKey];
        return acc;
      }, {}),
    ignoreSkuList: isLinkedTarget
      ? true
      : salesTargetData?.tabWiseData?.parent?.ignoreSkuList,
    targetType: salesTargetData?.targetTypeValue || '',
    excludeTaxFromGoal: salesTargetData?.excludeTaxFromGoal,
    offlineOrdersIncluded: salesTargetData?.offlineOrdersIncluded,
    roundUpPointsFlag: salesTargetData?.roundUpPointsFlag,
    joinCampaignFlag: salesTargetData?.joinCampaignFlag,
    roundPointsValue:
      salesTargetData?.roundPointsValue || salesTargetData?.roundOffValue || '',
    VATIncluded: !salesTargetData?.excludeTaxFromGoal,
    cepRefId: salesTargetData?.cepRefId,
    displayStartDate: getISOTimeStringForReview(
      salesTargetData?.displayStartDate,
      false,
      true
    ),
    displayEndDate: getISOTimeStringForReview(
      salesTargetData?.displayEndDate,
      true,
      false
    ),
    activeStartDate: getISOTimeStringForReview(
      salesTargetData?.activeStartDate,
      false,
      true
    ),
    activeEndDate: getISOTimeStringForReview(
      salesTargetData?.activeEndDate,
      true,
      false
    ),
    targetDeliveryDate: getISOTimeStringForReview(
      salesTargetData?.targetDeliveryDate,
      true,
      false
    ),
    pointsDeliveryDate: getISOTimeStringForReview(
      salesTargetData?.pointsDeliveryDate,
      true,
      false
    ),
    pointDisplay: isLayeredTarget ? salesTargetData?.pointDisplay : null,
    ruleSet: ruleSet,
    media: getValidMediaData(salesTargetData),
    childRuleSet: childRuleSet,
    status: salesTargetData?.status,
    awardPointsImmedFlag: isMainTargetOrProdTarget
      ? salesTargetData?.awardPointsImmedFlag
      : null,
    activeDisplay: salesTargetData?.activeDisplay ?? false,
    storeList: salesTargetData?.storeList,
    linkedSalesTargetInfo: salesTargetData?.linkedSalesTargets || [],
    metadata: {
      productFileName:
        salesTargetData?.selectedType === Constants.UploadedProducts
          ? salesTargetData?.tabWiseData?.parent?.mediaFile[0]
          : null,
      isProductIdsUploaded:
        salesTargetData?.selectedType === Constants.UploadedProducts,
      numberLevel: isLayeredTarget ? salesTargetData?.numberLevel : null,
      numberMileStone: isLayeredTarget ? salesTargetData?.numberMileStone : null
    }
  };

  if (
    isProductGroupQuantityType(salesTargetData?.targetType) ||
    isProductGroupQuantityType(salesTargetData?.targetTypeValue)
  ) {
    result.ruleSet = null;
    result.childRuleSet = null;
    result.pointDisplay = null;
    result.productGtins = normalizeProductGtins(salesTargetData?.productGtins);
    result.awardPointsImmedFlag =
      salesTargetData?.awardPointsImmedFlag ?? false;
  }

  if (isCdaSuperTargetType(salesTargetData?.targetType)) {
    result.ruleSet = null;
    result.childRuleSet = null;
    result.pointDisplay = null;
    result.awardPointsImmedFlag = false;
  }

  // Append Early Payment-specific fields
  if (isLinkedTarget) {
    result.pointsDeliveryDate = getISOTimeStringForReview(
      salesTargetData?.pointsDeliveryDate,
      true,
      false
    );
    const isDropSize =
      salesTargetData?.targetType === TARGET_TYPES.DropSizeTarget;
    result.awardPointsImmedFlag = isDropSize
      ? false
      : (salesTargetData?.linkedSalesTargets || []).length === 0;
  }

  return result;
};

// Helper function to create request object for prerequisite targets
export const createPreReqReqObj = async (salesTargetData, categoryList) => {
  const ruleSet = await transformCategoriesToLabels(
    salesTargetData?.tabWiseData?.parent?.ruleSet,
    categoryList
  );
  const dateFields = formatDateFields(salesTargetData);

  const result = {
    storeList: salesTargetData?.storeList || [],
    targetName: salesTargetData?.targetName || '',
    ruleSet,
    targetType: salesTargetData?.targetTypeValue || '',
    awardPointsImmedFlag: false,
    joinCampaignFlag: salesTargetData?.joinCampaignFlag,
    VATIncluded: !salesTargetData?.excludeTaxFromGoal,
    cepRefId: salesTargetData?.cepRefId ?? null,
    childRuleSet: null,
    roundPointsValue:
      salesTargetData?.roundPointsValue || salesTargetData?.roundOffValue || '',
    roundUpPointsFlag: salesTargetData?.roundUpPointsFlag ?? false,
    excludeTaxFromGoal: salesTargetData?.excludeTaxFromGoal ?? false,
    ...dateFields,
    media: getValidMediaData(salesTargetData),
    displayContent: filterDisplayContent(salesTargetData?.displayContent, true),
    ignoreSkuList: salesTargetData?.tabWiseData?.parent?.ignoreSkuList ?? false,
    offlineOrdersIncluded: salesTargetData?.offlineOrdersIncluded ?? false,
    pointDisplay: null,
    child: await createChildObject(salesTargetData, dateFields, categoryList),
    application: null,
    parent: null,
    totalTasks: null,
    metadata: createMetadata(salesTargetData, 'parent')
  };

  return result;
};

/**
 * Hook for sales target form formatting and request object building.
 * Returns pure functions with no side effects.
 */
export const useSalesTargetsForm = () => {
  return {
    formatDataForStep,
    filterDisplayContent,
    formatDateFields,
    createMetadata,
    createApplicationObject,
    createChildObject,
    transformCategoriesToLabels,
    createReqObj,
    createPreReqReqObj,
    shouldTransformRuleSetOfSalesTarget,
    getDisplayContent,
    formatDraftNotificationMessage
  };
};
